"use client"

import styled from "@emotion/styled"
import { Button, Typography } from "@mui/material"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import {
  AnimatedTypography,
  transitionDelayTime,
  TransitionUp,
  useInViewConfig,
} from "ui"
import Container from "ui/Container"
import {
  extractProperty,
  getNonUmbracoUrl,
  Link,
  methodsPaymentPublicTransport,
} from "utils"

type Props = {
  data: any
}

const StyledDiv = styled.div`
  padding-top: 48px;
  width: 100%;
`

const StyledButtonWrapper = styled.div`
  border: 1px solid #d1d5db;
  background-color: #fcfcfc;
  height: auto;
  padding: 4px;
  width: 100%;
  border-radius: 100px;
  margin-top: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: fit-content;
  }
`

const StyledButton = styled(Button)<{ isActive: boolean }>`
  background-color: ${({ isActive }) => (isActive ? "#008761" : "white")};
  color: ${({ isActive }) => (isActive ? "white" : "#051711")};
  padding: 13px 22px;
  border-radius: 100px;
  ${({ theme }) => theme.breakpoints.down("md")} {
    width: 50%;
  }
`

const StyledVisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

const StyledItemsDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 24px;
  }

  padding-top: 24px;
  width: auto;
`

const StyledItemDiv = styled.div`
  box-shadow: 0px 2px 8px 0px #26303d29;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const ImageContainer = styled.div`
  width: 100%;
  height: 48px;
  position: relative;
`

const StyledLinksDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 24px;
`
const StyledLink = styled(Link)`
  text-decoration: none;
  color: #6f747d;
  font-size: 18px;
  width: 230px;
  border: 1px solid #6f747d;
  border-radius: 12px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => theme.breakpoints.down("md")} {
    width: auto;
    padding-left: 32px;
    padding-right: 32px;
    text-align: center;
    height: auto;
    min-height: 48px;
    padding-top: 2px;
    padding-bottom: 2px;
  }
`

const PaymentMethods = ({ data }: Props) => {
  const title = extractProperty(data, "title")
  const itemsList = extractProperty(data, "itemsList")
  const { ref, inView } = useInView(useInViewConfig)
  const toggleList = itemsList.map((el: any) => extractProperty(el, "toggle"))
  const uniqueToggleList = Array.from(new Set(toggleList))
  const [activeButton, setActiveButton] = useState(uniqueToggleList?.[0])
  const [filteredItems, setFilteredItems] = useState<any[]>([])
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({})

  useEffect(() => {
    if (itemsList && activeButton) {
      const newFilteredItems = itemsList.filter(
        (item: any) => extractProperty(item, "toggle") === activeButton,
      )?.[0]
      setFilteredItems(extractProperty(newFilteredItems, "itemsList"))
    }
  }, [itemsList, activeButton])

  const handleButton = (toggle: string) => {
    setActiveButton(toggle)
    methodsPaymentPublicTransport(toggle)
  }

  const handleKeyDown = (event: React.KeyboardEvent, toggle: string) => {
    const currentIndex = uniqueToggleList.indexOf(toggle)
    let newIndex = currentIndex

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault()
        newIndex = (currentIndex + 1) % uniqueToggleList.length
        setActiveButton(uniqueToggleList[newIndex] as string)
        // Focus the new button
        setTimeout(() => {
          buttonRefs.current[uniqueToggleList[newIndex] as string]?.focus()
        }, 0)
        break
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault()
        newIndex =
          currentIndex === 0 ? uniqueToggleList.length - 1 : currentIndex - 1
        setActiveButton(uniqueToggleList[newIndex] as string)
        // Focus the new button
        setTimeout(() => {
          buttonRefs.current[uniqueToggleList[newIndex] as string]?.focus()
        }, 0)
        break
      case "Enter":
      case " ":
        event.preventDefault()
        handleButton(toggle)
        break
    }
  }

  return (
    <StyledDiv ref={ref}>
      <Container paddingMobile>
        <AnimatedTypography
          $inview={inView}
          variant="h2"
          id="payment-methods-title"
        >
          {title}
        </AnimatedTypography>
        <StyledVisuallyHidden>
          Choose payment method category
        </StyledVisuallyHidden>
        <StyledButtonWrapper
          role="radiogroup"
          aria-labelledby="payment-methods-title"
        >
          {uniqueToggleList.map((toggle: any, index: number) => (
            <StyledButton
              key={index}
              ref={(el: any) => (buttonRefs.current[toggle] = el)}
              isActive={toggle === activeButton}
              onClick={() => handleButton(toggle)}
              onKeyDown={(event: any) => handleKeyDown(event, toggle)}
              role="radio"
              aria-checked={toggle === activeButton}
              tabIndex={toggle === activeButton ? 0 : -1}
            >
              {toggle}
            </StyledButton>
          ))}
        </StyledButtonWrapper>
        <StyledItemsDiv>
          {filteredItems.map((item: any, index: number) => {
            const logo = extractProperty(item, "logo")
            const title = extractProperty(item, "title")
            const buttons = extractProperty(item, "buttons")

            return (
              <TransitionUp
                key={index}
                $inView={inView}
                $delay={transitionDelayTime * 0.5 * index}
                style={{ width: "auto" }}
              >
                <StyledItemDiv>
                  <ImageContainer>
                    <Image
                      src={getNonUmbracoUrl(logo)}
                      alt={title}
                      fill
                      style={{ objectFit: "contain" }}
                    />
                  </ImageContainer>
                  <Typography variant="body1" style={{ paddingTop: "16px" }}>
                    {title}
                  </Typography>
                  <StyledLinksDiv role="region" aria-label={title}>
                    {buttons.map((button: any, index: number) => {
                      return (
                        <StyledLink
                          key={index}
                          href={getNonUmbracoUrl(button?.url)}
                          target="_blank"
                        >
                          {button?.name}
                        </StyledLink>
                      )
                    })}
                  </StyledLinksDiv>
                </StyledItemDiv>
              </TransitionUp>
            )
          })}
        </StyledItemsDiv>
      </Container>
    </StyledDiv>
  )
}

export default PaymentMethods
